// src/pages/CategoryHub.js
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL;

export default function CategoryHub() {
  const { slug } = useParams();

  const { data: category, isLoading, error } = useQuery({
    queryKey: ['category', slug],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/api/servicecategory/${slug}/`);
      return res.data;
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white">
        Loading category...
      </div>
    );
  }

  if (error || !category) {
    toast.error('Category not found');
    return (
      <div className="min-h-screen flex items-center justify-center text-red-400">
        Category not found
      </div>
    );
  }

  const categoryName = category.name;
  const categoryLower = category.name.toLowerCase();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-x-hidden">
      {/* Background blobs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-yellow-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      <Helmet>
        <title>{categoryName} Services in Kenya | Hire Professionals on SErraND</title>
        <meta
          name="description"
          content={`Find trusted ${categoryLower} services across Kenya. Browse verified ${categoryLower} in major towns and locations with SErraND.`}
        />
        <link
          rel="canonical"
          href={`https://www.serrand.org/categories/${category.slug}`}
        />
      </Helmet>

      <section className="relative py-16 px-4 max-w-7xl mx-auto z-10">
        {/* Breadcrumbs */}
        <nav className="text-gray-400 mb-8 text-sm">
          <Link to="/" className="hover:text-white">Home</Link>
          <span className="mx-2">›</span>
          <Link to="/categories" className="hover:text-white">Categories</Link>
          <span className="mx-2">›</span>
          <span className="text-white">{categoryName}</span>
        </nav>

        {/* H1 */}
        <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
          {categoryName} Services in Kenya
        </h1>

        {/* Intro */}
        <p className="text-xl text-gray-300 max-w-4xl leading-relaxed mb-12">
          Looking for reliable and experienced {categoryLower} in Kenya?
          SErraND helps you connect with verified professionals offering
          {` ${categoryLower}`} services across multiple locations, making it
          easier to hire with confidence.
        </p>

        {/* Educational SEO block */}
        <section className="max-w-5xl mb-16">
          <h2 className="text-3xl font-bold text-white mb-4">
            About {categoryName} Services
          </h2>

          <p className="text-gray-300 leading-relaxed mb-4">
            {categoryName} professionals play a critical role in maintaining,
            repairing, and improving homes, businesses, and equipment. Through
            SErraND, clients can find trusted {categoryLower} who have been
            vetted and listed based on real service offerings and availability.
          </p>

          <p className="text-gray-300 leading-relaxed">
            Each provider profile includes service details, location coverage,
            and contact options, allowing you to compare professionals before
            choosing the right one for your needs.
          </p>
        </section>

        {/* Locations */}
        <h2 className="text-3xl font-bold text-white mb-8">
          Popular Locations for {categoryName}
        </h2>

        {category.popular_locations && category.popular_locations.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {category.popular_locations.map((loc) => (
              <Link
                key={loc.slug}
                to={`/categories/${category.slug}/${loc.slug}`}
                className="bg-gray-800/60 backdrop-blur-lg border border-gray-700 rounded-xl p-6 hover:border-purple-500 transition-all hover:shadow-xl group"
              >
                <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-purple-400 transition-colors">
                  {categoryName} in {loc.name}
                </h3>
                <p className="text-gray-300">
                  {loc.count} {loc.count === 1 ? 'Provider' : 'Providers'} available
                </p>
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-gray-400">
            No locations available for this category yet.
          </p>
        )}

        {/* Bottom internal link reinforcement */}
        <div className="mt-20 text-center">
          <Link
            to="/categories"
            className="text-purple-400 hover:text-purple-300 text-lg font-medium"
          >
            Browse all service categories →
          </Link>
        </div>
      </section>
    </div>
  );
}
